import { inject, Injectable } from '@angular/core';
import { produce } from 'immer';
import { map, Observable, of, switchMap } from 'rxjs';

import { CanvasElement } from '@blaze-runtime/engine-domain';
import { PageInfo } from '@blaze-shared/api-contracts-domain';
import {
  DataBindingSourceInfo,
  ElementRedirect,
  FormContainerModelField,
  getActiveBinding,
  PregenerationType,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { ColumnLike, DatasetColumn, findColumn } from '@blaze-shared/schema-domain';
import { singularize, toTitleCase } from '@blaze-shared/shared-util';
import { getValueByPath, isDatasetNode, Workflow } from '@blaze-shared/workflow-domain';

import { hasDataProps, hasProps } from '../canvas-elements/element-model/element-props-guards';
import { isVariantOfFormContainer } from '../canvas-elements/element-model/element-type-guards';
import { LayoutsApiService } from '../data/layouts-api.service';
import { FormContainerPropsModel } from '../elements/form-container/form-container.model';
import { elementsToEntityState, entityStateToElements } from '../utils/elements/element.utils';

import { LayoutLoadingService } from './layout-loading.service';

@Injectable({ providedIn: 'root' })
export class PregenerationUtilsService {
  private readonly schemaService = inject(SchemaService);
  private readonly layoutLoadingService = inject(LayoutLoadingService);
  private readonly layoutsApiService = inject(LayoutsApiService);

  getNewDialogName(
    elementOrDataSource: CanvasElement | DataBindingSourceInfo,
    pregenerationType: PregenerationType,
    pages: PageInfo[],
    workflows: Workflow[],
  ): string {
    let dsName = '';
    const dataSource = this.getElementDataSource(elementOrDataSource);

    if (dataSource) {
      if (dataSource.sourceType === 'dataset') {
        dsName = dataSource.name ?? '';
      } else {
        const wf = workflows.find((w) => w.id === dataSource.id);
        const dsNode = wf?.nodes.filter(isDatasetNode).find((n) => !!n.datasetId);
        dsName = this.schemaService.getTableName(dsNode?.datasetId);
      }
    }

    const nameRoot = pregenerationType === 'view-dialog' ? 'view' : pregenerationType;
    const name = this.getUniqueDialogPageName(
      `${toTitleCase(nameRoot)} ${singularize(dsName)}`,
      pages,
    );
    return name;
  }

  getUniqueDialogPageName(nameRoot: string, pages: PageInfo[]): string {
    const pagesMap = pages.reduce((acc, curr) => {
      acc[curr.name.toLocaleLowerCase()] = curr;
      return acc;
    }, {} as any);
    let name = nameRoot;
    let i = 1;
    while (pagesMap[name.toLocaleLowerCase()]) {
      name = `${nameRoot} ${i++}`;
    }
    return name;
  }

  /** @returns true when page doesn't exist yet or the dataSourceId changed,
   * false when no changes were made or schema changed
   * and was updated on the dialog form */
  tryUpdateDialogFormSchema(
    element: CanvasElement,
    actionPath: string | undefined,
    pages: PageInfo[],
    workflows: Workflow[],
  ): Observable<boolean> {
    const elementDataSource = this.getElementDataSource(element);
    if (!elementDataSource) {
      console.warn(`${element.id} (${element.type}): no elementDataSource for dialog`);
      return of(false);
    }

    const currentTarget =
      actionPath && hasProps(element)
        ? (getValueByPath(element.props, actionPath) as ElementRedirect)
        : undefined;
    const existingPage = currentTarget
      ? pages.find((p) => p.id === currentTarget.targetId)
      : undefined;
    if (!existingPage) {
      return of(true);
    }

    const elementMainDataSet =
      elementDataSource?.sourceType === 'dataset'
        ? elementDataSource.id
        : workflows
            .find((w) => w.id === elementDataSource.id)
            ?.nodes.filter(isDatasetNode)
            .find((n) => !!n.datasetId)?.datasetId;

    if (
      !elementMainDataSet ||
      !existingPage.mainDatasetId ||
      elementMainDataSet !== existingPage.mainDatasetId
    ) {
      return of(true);
    }

    return this.layoutLoadingService.getLayout(existingPage.id).pipe(
      switchMap((dialogLayout) => {
        const entitiesState = elementsToEntityState(dialogLayout.uiLayer.content);
        const dialogFormId = (entitiesState.ids as string[]).find((id) => {
          const el = entitiesState.entities[id];
          return isVariantOfFormContainer(el) && el.props.content.origin === 'generated';
        });
        const dialogFormElement = dialogFormId && entitiesState.entities[dialogFormId];
        if (!dialogFormElement || !hasProps(dialogFormElement)) {
          return of(true);
        }
        const elementMainDataSetSchema = this.schemaService.getDatasetSchema(elementMainDataSet);
        if (!elementMainDataSetSchema) {
          throw new Error(`dataset ${elementMainDataSet} schema is missing`);
        }
        const formDataColumns = this.getElementDataSource(dialogFormElement)?.columns ?? [];
        const dialogFormProps = dialogFormElement.props as FormContainerPropsModel;
        const formModel = dialogFormProps.content.model;

        let updateSchema = false;
        for (const formField of formModel) {
          if (formField.isCustom) {
            continue;
          }

          const formColumn = findColumn(formDataColumns, {
            id: formField.fieldId,
            name: formField.field,
          });
          const schemaColumn = findColumn(elementMainDataSetSchema.columns, {
            id: formField.fieldId,
            name: formField.field,
          });
          // column for visible input was removed
          if (formField.visible && !schemaColumn) {
            return of(true);
          }

          if (
            !formColumn ||
            !schemaColumn ||
            formColumn.name !== schemaColumn.name ||
            formColumn.dataType !== schemaColumn.dataType ||
            formColumn.columnType !== schemaColumn.columnType ||
            formColumn.isRequired !== schemaColumn.isRequired ||
            formColumn.multipleValues !== schemaColumn.multipleValues ||
            formColumn.isUnique !== schemaColumn.isUnique
          ) {
            updateSchema = true;
            break;
          }
        }
        let newColumns: DatasetColumn[] = [];
        const fieldsIds = formModel.map((f) => ({ id: f.fieldId, name: f.fieldId }) as ColumnLike);
        newColumns = elementMainDataSetSchema.columns.filter(
          (c) => !c.isId && !findColumn(fieldsIds, c),
        );
        updateSchema ||= newColumns.length > 0;

        if (updateSchema) {
          entitiesState.entities[dialogFormId] = produce(dialogFormElement, (draft) => {
            const dataProps = hasDataProps(draft) ? draft.props.data : undefined;
            const dataSource = getActiveBinding(dataProps, 'value')?.dataSource;
            if (!dataSource) {
              throw Error('dataSource missing');
            } //sanity check
            dataSource.columns = elementMainDataSetSchema.columns;
            dataSource.displayColumn = elementMainDataSetSchema.displayColumn;
            dataSource.name = elementMainDataSetSchema.name;

            const updatedModel = dialogFormProps.content.model
              .map((mf) => {
                const schemaColumn = findColumn(elementMainDataSetSchema.columns, {
                  id: mf.fieldId,
                  name: mf.field,
                });
                if (!schemaColumn) {
                  return undefined;
                }

                mf.columnType = schemaColumn.columnType;
                mf.dataType = schemaColumn.dataType;
                mf.field = schemaColumn.name;
                return mf;
              })
              .filter(Boolean);
            if (newColumns.length) {
              updatedModel.push(
                ...newColumns.map(
                  (c) =>
                    ({
                      fieldId: c.id,
                      field: c.name,
                      value: null,
                      columnType: c.columnType,
                      dataType: c.dataType,
                      visible: false,
                    }) as FormContainerModelField,
                ),
              );
            }
            (draft.props as any).content.model = updatedModel;
          });

          return this.layoutsApiService
            .updateLayouts([
              {
                ...dialogLayout,
                uiLayer: { content: entityStateToElements(entitiesState) },
              },
            ])
            .pipe(map(() => false));
        }

        return of(false);
      }),
    );
  }

  private getElementDataSource(
    elementOrDataSource: CanvasElement | DataBindingSourceInfo,
  ): DataBindingSourceInfo | undefined {
    if ('sourceType' in elementOrDataSource) {
      return elementOrDataSource as DataBindingSourceInfo;
    }

    if (!hasDataProps(elementOrDataSource)) {
      return;
    }

    return elementOrDataSource.props.data.find((data) => data?.key === 'value')?.dataSource;
  }
}
